import type { ToolCall } from '../../../../../utils/interface-types';
import { WrenchIcon } from '../../../../../assets/images';
import ToolCallCard from '../tool-call-card';
import {
  formatToolDisplayName,
  formatToolLiveStatus,
  summarizeToolCalls,
  toolCallHasVisualOutput,
  toolCallIsComplete,
} from '../../lib/mcp-tool-utils';

type ToolsUsedPanelProps = {
  toolCalls: ToolCall[];
  expanded: boolean;
  onToggle: () => void;
  compact?: boolean;
};

function ToolSpinner() {
  return (
    <span
      className="inline-block w-3 h-3 rounded-full border-2 border-[#0A8BC7]/30 border-t-[#0A8BC7] animate-spin"
      aria-hidden
    />
  );
}

function ToolStatusIcon({ toolCall }: { toolCall: ToolCall }) {
  if (toolCall.mcp_error) {
    return <span className="text-red-500 text-xs font-medium">Failed</span>;
  }
  if (toolCallIsComplete(toolCall)) {
    return (
      <span className="text-green-600 text-xs font-medium flex items-center gap-1">
        <span aria-hidden>✓</span>
        <span className="sr-only">Completed</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-[#0A8BC7]">
      <ToolSpinner />
      <span className="truncate max-w-[12rem]">{formatToolLiveStatus(toolCall.tool_name)}</span>
    </span>
  );
}

function PanelStatusBadge({ toolCalls }: { toolCalls: ToolCall[] }) {
  const { anyError, allComplete, anyLoading } = summarizeToolCalls(toolCalls);

  if (anyLoading) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[#0A8BC7]">
        <ToolSpinner />
        Running
      </span>
    );
  }
  if (!allComplete) {
    return null;
  }
  if (anyError) {
    return <span className="text-xs text-red-600">Some failed</span>;
  }
  return <span className="text-xs text-green-600">Success</span>;
}

function ToolsUsedPanel({ toolCalls, expanded, onToggle, compact = false }: ToolsUsedPanelProps) {
  if (!toolCalls.length) {
    return null;
  }

  const { anyLoading } = summarizeToolCalls(toolCalls);
  const visualToolCalls = toolCalls.filter((toolCall) => toolCallHasVisualOutput(toolCall));
  const canExpand = visualToolCalls.length > 0 || compact;
  const showToolList = !compact || expanded || anyLoading;

  const panelClass = compact
    ? 'tools-used-panel tools-used-panel--compact mb-1 mt-3 border-0 bg-transparent'
    : 'tools-used-panel mb-2 rounded-lg border border-zinc-100 bg-zinc-50/40';

  const chipClassName = `tools-used-panel-chip flex items-center gap-2 transition-colors ${
    canExpand ? 'cursor-pointer hover:opacity-80' : ''
  } ${compact ? '' : 'rounded-lg p-2'}`;

  const chipLabel = (
    <>
      <img src={WrenchIcon} alt="" className="w-3.5 h-3.5 opacity-50" />
      <span className={`text-xs flex-1 ${anyLoading ? 'text-[#0A8BC7]' : 'text-zinc-500'}`}>
        {anyLoading ? (
          'Using tools…'
        ) : (
          <>
            {toolCalls.length} tool{toolCalls.length === 1 ? '' : 's'}
            {compact ? null : ' used'}
          </>
        )}
        {compact && !anyLoading ? (
          <>
            {' · '}
            <PanelStatusBadge toolCalls={toolCalls} />
          </>
        ) : null}
      </span>
      {!compact ? <PanelStatusBadge toolCalls={toolCalls} /> : null}
      {canExpand ? (
        <svg
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      ) : null}
    </>
  );

  return (
    <div className={panelClass}>
      {canExpand ? (
        <button
          type="button"
          className={`${chipClassName} w-full border-0 bg-transparent p-0 font-inherit text-inherit`}
          onClick={onToggle}
          aria-expanded={expanded}
        >
          {chipLabel}
        </button>
      ) : (
        <div className={chipClassName}>{chipLabel}</div>
      )}

      {showToolList ? (
        <ul className={`space-y-1 ${compact ? 'mt-1.5 pl-1' : 'px-2 pb-2'}`}>
          {toolCalls.map((toolCall, index) => (
            <li
              key={toolCall.id || `${toolCall.tool_name}-${index}`}
              className="flex items-center justify-between gap-3 rounded-md bg-white px-2 py-1 border border-zinc-100"
            >
              <span className="text-xs text-zinc-700 truncate">
                {formatToolDisplayName(toolCall.tool_name)}
              </span>
              <ToolStatusIcon toolCall={toolCall} />
            </li>
          ))}
        </ul>
      ) : null}

      {visualToolCalls.length > 0 && (
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            compact ? 'border-0' : 'border-t border-zinc-100'
          } ${expanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <div className={`space-y-3 ${compact ? 'pt-2' : 'p-2'}`}>
            {visualToolCalls.map((toolCall, toolIndex) => (
              <ToolCallCard
                key={toolCall.id || `${toolCall.tool_name}-${toolIndex}`}
                toolCall={toolCall}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

ToolsUsedPanel.defaultProps = {
  compact: false,
};

export default ToolsUsedPanel;
